<div class="loads-shell company-directory-shell fade-in" [class.rtl]="languageService.currentLanguage() === 'ar'">
  <section class="page-hero">
    <div class="hero-content">
      <span class="eyebrow">Company Directory</span>
      <h1 class="hero-title">Company Directory</h1>
      <p class="hero-copy">
        Search trusted logistics companies by name, specialization, lane, or operating region.
      </p>

      <div class="hero-search-summary">
        @if (searchSummary.length === 0) {
          <span class="summary-empty">No filters applied</span>
        } @else {
          @for (item of searchSummary; track item.label) {
            <span class="summary-chip">
              <strong>{{ item.label }}</strong>
              {{ item.value }}
            </span>
          }
        }
      </div>
    </div>

    <div class="hero-side">
      <div class="hero-stats">
        <div class="stat-card">
          <fa-icon [icon]="icons.building"></fa-icon>
          <span class="stat-value">{{ totalCompanies }}</span>
          <span class="stat-label">Companies</span>
        </div>
        <div class="stat-card">
          <fa-icon [icon]="icons.list"></fa-icon>
          <span class="stat-value">{{ pageSize }}</span>
          <span class="stat-label">Per page</span>
        </div>
      </div>
    </div>
  </section>

  <div class="market-layout">
    <main class="market-main">
      <section class="market-panel">
        <header class="panel-header">
          <div>
            <div class="panel-kicker">Directory Board</div>
            <h2>{{ totalCompanies }} Companies Found</h2>
            <p>Verified network records with trust, activity, services, and operating regions.</p>
          </div>
        </header>

        <div class="results-summary">
          <span>Page {{ currentPage + 1 }}</span>
          <div class="pagination-controls">
            <button type="button" class="pagination-btn" (click)="previousPage()" [disabled]="!canGoPrevious">
              <fa-icon [icon]="icons.chevronLeft"></fa-icon>
              Previous
            </button>
            <span class="page-chip">{{ currentPage + 1 }} / {{ displayTotalPages }}</span>
            <button type="button" class="pagination-btn" (click)="nextPage()" [disabled]="!canGoNext">
              Next
              <fa-icon [icon]="icons.chevronRight"></fa-icon>
            </button>
          </div>
        </div>

        @if (entries.length > 0) {
          <div class="company-grid">
            @for (entry of entries; track entry.id) {
              <article class="match-card company-card">
                <div class="card-topline">
                  <div class="title-wrap">
                    <div class="company-logo">
                      @if (logoUrl(entry); as logo) {
                        <img [src]="logo" [alt]="entry.name || 'Company logo'" (error)="markLogoFailed(entry)" />
                      } @else {
                        <span>{{ logoInitial(entry) }}</span>
                      }
                    </div>
                    <span class="status-pill">{{ companyTypeLabel(entry.companyType) }}</span>
                  </div>
                  <div class="rate-box">
                    <span>Trust score</span>
                    <strong>{{ scoreLabel(entry.trustScore) }}</strong>
                  </div>
                </div>

                <div class="company-title-block">
                  <h3>{{ entry.name || 'Unnamed company' }}</h3>
                  @if (entry.companyClass) {
                    <span class="subtle-text">{{ entry.companyClass }}</span>
                  }
                </div>

                <div class="signal-strip">
                  @if (entry.verificationStatus && entry.verificationStatus !== 'UNVERIFIED') {
                    <span class="signal-chip positive">
                      <fa-icon [icon]="icons.check"></fa-icon>
                      {{ entry.verificationStatus }}
                    </span>
                  }
                  @if (entry.lastActiveAt) {
                    <span class="signal-chip">
                      <fa-icon [icon]="icons.chart"></fa-icon>
                      Last active {{ formatDate(entry.lastActiveAt) }}
                    </span>
                  }
                </div>

                <div class="route-stack company-metrics">
                  <div class="route-point">
                    <span class="route-dot origin-dot"></span>
                    <div>
                      <strong>{{ entry.totalLoadsPosted }} loads posted</strong>
                      <small>{{ entry.completedLoads }} completed, {{ entry.cancelledLoads }} cancelled</small>
                    </div>
                  </div>
                  <div class="route-point">
                    <span class="route-dot destination-dot"></span>
                    <div>
                      <strong>{{ entry.activeLoadsCount }} active loads</strong>
                      <small>{{ formatCompletion(entry.completionRate) }} completion rate</small>
                    </div>
                  </div>
                </div>

                <div class="tag-row">
                  @if ((entry.specializations || []).length > 0) {
                    @for (spec of (entry.specializations || []).slice(0, 3); track spec) {
                      <span class="info-chip">
                        <fa-icon [icon]="icons.briefcase"></fa-icon>
                        {{ spec }}
                      </span>
                    }
                  }
                  @if ((entry.operatingRegions || []).length > 0) {
                    @for (region of (entry.operatingRegions || []).slice(0, 2); track region) {
                      <span class="info-chip">
                        <fa-icon [icon]="icons.location"></fa-icon>
                        {{ region }}
                      </span>
                    }
                  }
                </div>

                <div class="trust-strip {{ trustScoreClass(entry.trustScore) }}">
                  <fa-icon [icon]="icons.shield"></fa-icon>
                  <span>{{ entry.trustBadge || 'Trust profile not available' }}</span>
                  <strong>{{ scoreLabel(entry.trustScore) }}/100</strong>
                </div>

                <div class="card-actions">
                  <button type="button" class="secondary-action" (click)="viewCompany(entry.id)">
                    <fa-icon [icon]="icons.eye"></fa-icon>
                    View Details
                  </button>
                </div>
              </article>
            }
          </div>
        } @else if (loading()) {
          <div class="state-panel">
            <div class="spinner"></div>
            <p>Loading company directory...</p>
          </div>
        } @else if (error) {
          <div class="state-panel error">{{ error }}</div>
        } @else {
          <div class="state-panel">
            <fa-icon [icon]="icons.building"></fa-icon>
            <p>No companies match your search.</p>
          </div>
        }
      </section>
    </main>

    <aside class="market-side">
      <section class="action-card filters-panel">
        <header class="filter-card-header">
          <div>
            <div class="panel-kicker">Refine</div>
            <h3>Filters</h3>
            <p>Search by company, specialization, lane, region, and company type.</p>
          </div>
          <button type="button" class="clear-button" (click)="clearFilters()">
            <fa-icon [icon]="icons.filterClear"></fa-icon>
            Clear
          </button>
        </header>

        <form class="filters-grid side-filters-grid" (ngSubmit)="search()" [formGroup]="filtersForm">
          <div class="filter-field">
            <p-inputGroup>
              <p-inputGroupAddon><fa-icon [icon]="icons.search"></fa-icon></p-inputGroupAddon>
              <p-iftaLabel>
                <input pInputText id="companySearch" formControlName="searchText" />
                <label for="companySearch">Search company, lane, or region</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          <div class="type-filter-panel">
            <div class="filter-label">Company type</div>
            @for (t of companyTypes; track t.key) {
              <button
                type="button"
                class="type-filter-btn"
                [class.active]="selectedCompanyType === t.key"
                (click)="setTypeFilter(t.key)">
                {{ t.labelEn }}
              </button>
            }
          </div>

          <button type="submit" class="apply-button">
            <fa-icon [icon]="icons.search"></fa-icon>
            Search
          </button>
        </form>
      </section>
    </aside>
  </div>
</div>
